import React, {useEffect, useRef, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../../modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import Overlay from 'app/components/Overlay'
import InfoCard from '../drawer-components/InfoCard'
import DrawerFooter from '../drawer-components/DrawerFooter'
import {AddDrawerIntegrationProps} from '../utils/types'

const NOTION_CLIENT_ID = process.env.REACT_APP_NOTION_CLIENT_ID || ''
const NOTION_REDIRECT_URI = process.env.REACT_APP_NOTION_REDIRECT_URI || ''
const NOTION_AUTH_URL = 'https://api.notion.com/v1/oauth/authorize'

const NotionDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const [notionAccessToken, setNotionAccessToken] = useState('')
  const [notionWorkspaceName, setNotionWorkspaceName] = useState('')
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'

  useEffect(() => {
    if (isOpen && id) {
      getIntegration()
    }
  }, [id, isOpen])

  useEffect(() => {
    if (isConnected && integration[0]) {
      setNotionAccessToken(integration[0].accessToken || '')
      setNotionWorkspaceName(integration[0].workspaceName || '')
    } else {
      setNotionAccessToken('')
      setNotionWorkspaceName('')
    }
  }, [integration, isConnected])

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Notion') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
            }
            setIntegration([integrationData])
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const handleConnect = async () => {
    if (!notionAccessToken) {
      setIsLoading(true)
      localStorage.setItem('pendingIntegration', 'Notion')
      const encodedRedirectUri = encodeURIComponent(NOTION_REDIRECT_URI)
      const authUrl = `${NOTION_AUTH_URL}?client_id=${NOTION_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&owner=user`
      window.location.href = authUrl
      return
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetsRef = collection(tenantRef, 'assets')
      const notionAssetsQuery = query(assetsRef, where('platform', '==', 'Notion'))
      const notionAssetsSnapshot = await getDocs(notionAssetsQuery)

      const deleteAssetPromises = notionAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id))
      })

      await Promise.all(deleteAssetPromises)

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)

      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'notion')
      await deleteDoc(accessReviewRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const notionVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Notion'))
      const notionVendorSnapshot = await getDocs(notionVendorQuery)

      const deleteVendorPromises = notionVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      localStorage.removeItem('notion_access_token')

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Notion integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'Notion integration disconnected'})

      setIntegration([])
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error disconnecting Notion:', errorMessage)
      showErrorToast({message: 'Failed to disconnect Notion'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Notion' onClose={onClose} />

      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='Notion'
            description='Issue Tracking & Project Management'
            logo='/media/integrations/notion.svg'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted permissions to read Notion pages and databases via OAuth 2.0, respecting user permissions.'
            accessTypeinfo='Requires an active Notion integration with sufficient permissions to access and modify pages and databases.'
          />

          {!isConnected &&
          <>
            <div className='mb-8 d-flex justify-content-between align-items-center'>
              <div className='fs-6 fw-bold text-bold'>
                {isConnected ? 'Notion Connected to SecureSlate' : 'Connect Notion to SecureSlate'}{' '}
                <Overlay
                  content='Upon successful connection, all pages and databases within your designated
                        Notion workspace will be automatically imported and presented within the
                        Asset Repository section of the platform for your review and management.'
                />
              </div>
            </div>
            <InfoCard
              type='info'
              title='How to connect Notion with SecureSlate?'
              description='Click on the "Connect" button below. You will be redirected to Notion to authenticate via OAuth and authorize SecureSlate. Once authorized, pages and databases will be automatically fetched and configured.'
            />
          </>
          }
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default NotionDrawer
